/**
 * Mermaid Emitter
 *
 * Renders the DI graph as design.md — a Mermaid `graph TD` diagram that GitHub
 * and IDEs render inline, so the DI design is reviewable in every PR. Emitted
 * in the same sorted order as design.json so diffs are equally stable.
 */

import { DiDesign, DiGraph, DiNode } from './model';
import { isStackedNode } from './dot';
import { sortGraph } from './serializer';

/** Mermaid node ids must be simple identifiers — map graph ids to safe ids. */
function mermaidId(id: string): string {
    return id.replace(/[^A-Za-z0-9_]/g, '_');
}

/** Escape text destined for a quoted mermaid label. */
function label(text: string): string {
    return text.replace(/"/g, '#quot;');
}

function nodeStatement(node: DiNode): string {
    const id = mermaidId(node.id);
    // Injected as an API → contract on top, impl class in parens (leaves never set `api`).
    const text = node.api ? `${label(node.api)}<br/>(${label(node.className)})` : label(node.className);
    if (node.kind === 'controller') return `    ${id}["${text}"]:::controller`;
    if (node.kind === 'apiImplementation') return `    ${id}["${text}"]:::apiImpl`;
    if (node.kind === 'component') return `    ${id}["${text}"]:::component`;
    if (node.kind === 'constant' || node.kind === 'dynamic') return `    ${id}(["${text}"])`;
    if (node.kind === 'unresolved') return `    ${id}{{"${text} ?"}}:::unresolved`;
    // External boundary — subroutine box (double vertical bars) + distinct class.
    if (node.kind === 'external') return `    ${id}[["${text}"]]:::external`;
    // API-client proxy boundary — stadium subroutine (rounded) + distinct class.
    if (node.kind === 'api') return `    ${id}[["${text}"]]:::api`;
    // Transient (1-to-many). A plain box with a dashed accent, NOT mermaid's `@{ shape: procs }`
    // stack: that needs mermaid >= 11.3 and design.md is rendered by whatever version the viewer
    // ships (GitHub, an IDE preview) — this repo pins none. design.html draws the real stack.
    if (isStackedNode(node)) return `    ${id}["${text}"]:::many`;
    return `    ${id}["${text}"]`;
}

function graphBody(design: DiDesign): string[] {
    const lines: string[] = ['graph TD'];
    for (const node of design.nodes) {
        lines.push(nodeStatement(node));
    }
    for (const edge of design.edges) {
        const from = mermaidId(edge.from);
        const to = mermaidId(edge.to);
        // Unlabeled — the arrow alone shows the dependency; the param/field
        // name and token stay in design.json for tooling that wants them.
        lines.push(`    ${from} --> ${to}`);
    }
    lines.push('    classDef controller fill:#1f6feb,color:#ffffff,stroke:#0d419d');
    lines.push('    classDef apiImpl fill:#0d9488,color:#ffffff,stroke:#0f766e');
    lines.push('    classDef component fill:#2da44e,color:#ffffff,stroke:#1a7f37');
    lines.push('    classDef unresolved fill:#f0ad4e,color:#000000,stroke:#b8860b,stroke-dasharray: 5 5');
    lines.push('    classDef external fill:#b39ddb,color:#000000,stroke:#5e35b1,stroke-width:3px');
    lines.push('    classDef api fill:#4fc3f7,color:#000000,stroke:#0277bd,stroke-width:3px');
    // TRANSIENT: every arrow in resolves its own instance (a singleton's arrows share one).
    lines.push('    classDef many fill:#eceff1,color:#000000,stroke:#546e7a,stroke-width:2px,stroke-dasharray: 4 2');
    return lines;
}

/** One `## Root` section with the design's Mermaid diagram. */
function designSection(design: DiDesign): string[] {
    const kind =
        design.rootKind === 'controller'
            ? 'controller'
            : design.rootKind === 'apiImplementation'
              ? 'api-impl'
              : design.rootKind === 'component'
                ? 'component'
                : 'root';
    const heading = `## ${design.root} — ${kind}, Level 0…${design.maxLevel}`;
    return [heading, '', '```mermaid', ...graphBody(design), '```', ''];
}

/**
 * Render the design.md content for a project's DI graph — one Mermaid diagram
 * per design (per controller, or per library top-of-DAG root), each rooted at
 * Level 0 with its injections fanning downward.
 */
export function toDesignMarkdown(graph: DiGraph): string {
    sortGraph(graph);

    const header = [
        `# DI Design Graph — ${graph.project}`,
        '',
        `> GENERATED by \`nx run ${graph.project}:di-graph-generate\` — do not edit by hand.`,
        `> Machine-readable version: [design.json](./design.json)`,
        '',
        `Each section below is one root's dependency tree: Level 0 is the root`,
        `(a \`@DocumentDesign\` or top-of-DAG class), and constructor injections fan`,
        `downward through Levels 1, 2, … A dependency shared by multiple roots`,
        `appears in each root's tree.`,
        '',
    ];

    if (graph.designs.length === 0) {
        return header.concat(['No DI-registered classes found in this project.', '']).join('\n');
    }

    const sections: string[] = [];
    for (const design of graph.designs) {
        sections.push(...designSection(design));
    }

    const legend = [
        '',
        'Edges are constructor/`inject()` dependencies (the injected param/field',
        'name and token are in `design.json`). Rounded nodes are',
        '`toConstantValue`/`useValue` and `toDynamicValue`/`useFactory` leaves; dashed',
        'nodes are tokens the analyzer could not resolve; double-bordered nodes are',
        'boundaries shown but not expanded: violet = a class from a published package',
        'outside this workspace; cyan = a generated API-client proxy (`createApiClient`),',
        'i.e. a service/network boundary whose remote impl lives in another process.',
        '',
    ];

    return header.concat(sections).concat(legend).join('\n');
}
